import { Component, ComponentFactoryResolver, ComponentRef, Inject, Injector, Input, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, combineLatest, interval, of, ReplaySubject, Subscription, timer } from 'rxjs';
import { Widget, WidgetType, WidgetUserFilter } from 'src/app/classes';
import { AbstractWidgetInner, WidgetInnerData, WidgetInnerView } from 'src/app/widget-inner';
import { CustomerWidgetInnerComponent } from '../widget-inner-components/customer-widget-inner/customer-widget-inner.component';
import { CalendarWidgetInnerComponent } from '../widget-inner-components/calendar-widget-inner/calendar-widget-inner.component';
import { FileExplorerWidgetInnerComponent } from '../widget-inner-components/file-explorer-widget-inner/file-explorer-widget-inner.component';
import { UnknownTypeComponent } from '../widget-inner-components/unknown-type/unknown-type.component';
import { ContractWidgetInnerComponent } from '../widget-inner-components/contract-widget-inner/contract-widget-inner.component';
import { LocationWidgetInnerComponent } from '../widget-inner-components/location-widget-inner/location-widget-inner.component';
import { ContactpersonWidgetInnerComponent } from '../widget-inner-components/contactperson-widget-inner/contactperson-widget-inner.component';
import { IssueWidgetInnerComponent } from '../widget-inner-components/issue-widget-inner/issue-widget-inner.component';
import { OfferWidgetInnerComponent } from '../widget-inner-components/offer-widget-inner/offer-widget-inner.component';
import { AssignmentMailWidgetInnerComponent } from '../widget-inner-components/assignment-mail-widget-inner/assignment-mail-widget-inner.component';
import { IncidentMailWidgetInnerComponent } from '../widget-inner-components/incident-mail-widget-inner/incident-mail-widget-inner.component';
import { IFrameWidgetInnerComponent } from '../widget-inner-components/i-frame-widget-inner/i-frame-widget-inner.component';
import { ChartWidgetInnerComponent } from '../widget-inner-components/chart-widget-inner/chart-widget-inner.component';
import { WidgetMenuComponent, WidgetMenuComponentInputData } from '../widget-menu/widget-menu.component';
import * as globals from '../../../globals';
import { switchMap, take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { WidgetService } from 'src/app/services/widget.service';
import { WidgetViewStandaloneComponentService, WidgetViewStandaloneModes } from '../widget-view-standalone/widget-view-standalone-component-service';
import { SessionService } from 'src/app/lib/shared/services/session.service';
import { WidgetComponentService } from './widget-component-service';
import { WidgetBarPosition, WidgetTypeMap } from '../widget-globals';
import { UserFilterDialogComponent, UserFilterDialogResult } from '../user-filter-dialog/user-filter-dialog.component';
import { WIDGET_PROVIDER_SERVICE, WIDGET_STANDALONE_COMPONENT_SERVICE } from 'src/app/interfaces';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-widget',
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.css'],
  providers: [
    { provide: WIDGET_PROVIDER_SERVICE, useClass: WidgetComponentService },
    { provide: WIDGET_STANDALONE_COMPONENT_SERVICE, useExisting: WidgetViewStandaloneComponentService }
  ]
})
export class WidgetComponent implements OnInit, OnDestroy {

  @Input() set widget(widget: Widget) {
    this.widgetComponentService.setWidget(widget);
  }

  private _widgetInnerHost = new ReplaySubject<ViewContainerRef>(1);
  widgetInnerHost$ = this._widgetInnerHost.asObservable();
  @ViewChild('widgetInner', { static: true, read: ViewContainerRef }) set widgetInnerHost(inner: ViewContainerRef) {
    this._widgetInnerHost.next(inner);
  }

  widgetIcon: string = "menu";

  refreshMinutes: number = null;

  defaultUserFilterIcon: string = "filter_alt";
  defaultUserFilterColor: string = "white";
  defaultUserFilterBackgroundColor: string = "red";

  defaultChildViewControlIcon: string = "crop_square";
  defaultChildViewControlBackgroundColor: string = "#00000059";

  widgetContentType: string;

  @Input() deleteMode: boolean;

  data: WidgetInnerData = new WidgetInnerData();
  widgetInner: Type<AbstractWidgetInner>;
  widgetInnerViewSubscription: Subscription;
  componentRef: ComponentRef<AbstractWidgetInner>;

  rightSubscription: Subscription;
  rightsMap = {
    edit_dashboard: false
  };

  private _refreshing = new BehaviorSubject(false);
  refreshing$ = this._refreshing.asObservable();

  public refreshSub: Subscription = null;
  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(WIDGET_PROVIDER_SERVICE) public widgetComponentService: WidgetComponentService,
    public widgetViewStandaloneComponentService: WidgetViewStandaloneComponentService,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _dialog: MatDialog,
    private _sessionService: SessionService,
    private _widgetService: WidgetService,
    private _viewContainerRef: ViewContainerRef,
    private _snackBar: MatSnackBar
  ) { }

  chartViewClicked(view: CustomChartView) {
    if (this.componentRef && this.componentRef.instance) this.componentRef.instance.receiveChartView(view);
  }

  deleteWidget = () => this.widgetComponentService.widget$.pipe(switchMap((widget: Widget) => this.widgetViewStandaloneComponentService.deleteWidget(widget))).toPromise();

  iFrameClicked(href: string) {
    if (this.componentRef && this.componentRef.instance) this.componentRef.instance.receiveIFrame(href);
  }

  initRights = (soft: boolean = true) => {
    for (let index in this.rightsMap) this._sessionService.hasRight(index, soft).subscribe(result => this.rightsMap[index] = result);
  }

  initWidget(widget: Widget) {
    this.widgetComponentService.chartViews = [];
    this.widgetComponentService.widgetBarPosition = WidgetBarPosition.Left;
    this.widgetIcon = "menu";
    if (widget) {
      if (Array.isArray(widget.widgetsetting)) {
        for (let setting of widget.widgetsetting) {
          switch (setting.widgetsettingdesc) {
            case "toolbar-position":
              switch (setting.widgetsettingvalue) {
                case "top":
                case WidgetBarPosition.Top.toString():
                  this.widgetComponentService.widgetBarPosition = WidgetBarPosition.Top;
                  break;
                case "right":
                case WidgetBarPosition.Right.toString():
                  this.widgetComponentService.widgetBarPosition = WidgetBarPosition.Right;
                  break;
                case "bottom":
                case WidgetBarPosition.Bottom.toString():
                  this.widgetComponentService.widgetBarPosition = WidgetBarPosition.Bottom;
                  break;
                case "left":
                case WidgetBarPosition.Left.toString():
                  this.widgetComponentService.widgetBarPosition = WidgetBarPosition.Left;
                  break;
                case "none":
                case WidgetBarPosition.None.toString():
                  this.widgetComponentService.widgetBarPosition = WidgetBarPosition.None;
                  break;
              }
              break;
            case "custom-icon":
              this.widgetIcon = setting.widgetsettingvalue;
              break;
            case "custom-chart-view":
              this.widgetComponentService.addChartView(setting);
              break;
            case "iframe-href":
              this.widgetComponentService.addIFrameView(setting.widgetsettingvalue);
              break;
            case "refresh-minutes":
              this.refreshMinutes = setting.widgetsettingvalue ? +setting.widgetsettingvalue : null;
              this.updateRefreshInterval();
              break;
          }
        }
      }
    }
  }

  initWidgetType(widgetType: WidgetType) {
    let type = WidgetTypeMap[widgetType.wtypename];
    if (!type) {
      console.warn("unknown widget!", widgetType.wtypename);
      return;
    }
    switch (type) {
      case WidgetTypeMap.AvailabilityRequestWidget:
        this.widgetInner = LocationWidgetInnerComponent;
        this.widgetComponentService.contentType = "Location";
        break;
      case WidgetTypeMap.CalendarWidget:
        this.widgetInner = CalendarWidgetInnerComponent;
        break;
      case WidgetTypeMap.ChangeWidget:
        this.widgetInner = IssueWidgetInnerComponent;
        this.widgetComponentService.contentType = "Change";
        break;
      case WidgetTypeMap.ChartWidget:
        this.widgetInner = ChartWidgetInnerComponent;
        break;
      case WidgetTypeMap.ContactPersonalWidget:
        this.widgetInner = ContactpersonWidgetInnerComponent;
        this.widgetComponentService.contentType = "ContactPerson";
        break;
      case WidgetTypeMap.ContractWidget:
        this.widgetInner = ContractWidgetInnerComponent;
        this.widgetComponentService.contentType = "Contract";
        break;
      case WidgetTypeMap.CustomerWidget:
        this.widgetInner = CustomerWidgetInnerComponent;
        this.widgetComponentService.contentType = "Customer";
        break;
      case WidgetTypeMap.DocumentWidget:
        this.widgetInner = FileExplorerWidgetInnerComponent;
        break;
      case WidgetTypeMap.IFrameWidget:
        this.widgetInner = IFrameWidgetInnerComponent;
        break;
      case WidgetTypeMap.IncidentMailWidget:
        this.widgetInner = IncidentMailWidgetInnerComponent;
        break;
      case WidgetTypeMap.IncidentWidget:
        this.widgetInner = IssueWidgetInnerComponent;
        this.widgetComponentService.contentType = "Incident";
        break;
      case WidgetTypeMap.IssueMailWidget:
        this.widgetInner = AssignmentMailWidgetInnerComponent;
        break;
      case WidgetTypeMap.IssueWidget:
        this.widgetInner = IssueWidgetInnerComponent;
        this.widgetComponentService.contentType = "Issue";
        break;
      case WidgetTypeMap.OfferWidget:
        this.widgetInner = OfferWidgetInnerComponent;
        this.widgetComponentService.contentType = "Offer";
        break;
      case WidgetTypeMap.ProjectWidget:
        this.widgetInner = IssueWidgetInnerComponent;
        this.widgetComponentService.contentType = "Project";
        break;
    }
  }

  loadWidgetInner(widget: Widget, widgetType: WidgetType, host: ViewContainerRef) {
    this.initWidget(widget);
    this.initWidgetType(widgetType);
    host.clear();
    this.data.widget = this.widget;
    if (this.widgetInner) {
      const componentFactory = this._componentFactoryResolver.resolveComponentFactory(this.widgetInner);
      this.componentRef = host.createComponent<AbstractWidgetInner>(componentFactory, null, Injector.create({
        providers: [
          { provide: WIDGET_PROVIDER_SERVICE, useValue: this.widgetComponentService },
          { provide: WIDGET_STANDALONE_COMPONENT_SERVICE, useExisting: this.widgetViewStandaloneComponentService }
        ]
      }));
      this.componentRef.instance.data = this.data;

      if (this.widgetInnerViewSubscription) {
        this.widgetInnerViewSubscription.unsubscribe();
        this.widgetInnerViewSubscription = null;
      }
    } else {
      const componentFactory = this._componentFactoryResolver.resolveComponentFactory(UnknownTypeComponent);
      this.componentRef = host.createComponent<AbstractWidgetInner>(componentFactory);
      this.componentRef.instance.data = this.data;
      this.widgetComponentService.widgetBarPosition = WidgetBarPosition.None;
    }
    this.componentRef.changeDetectorRef.detectChanges();
    switch (widgetType.wtypename) {
      case "ChartWidget":
        if (this.widgetComponentService.chartViews.length > 0) this.componentRef.instance.receiveChartView(this.widgetComponentService.chartViews[0]);
        break;
    }
  }

  ngOnDestroy(): void {
    if (this.widgetInnerViewSubscription) {
      this.widgetInnerViewSubscription.unsubscribe();
      this.widgetInnerViewSubscription = null;
    }
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit(): void {
    this._subscriptions.push(...[
      this.widgetComponentService.widget$.pipe(
        switchMap((x: Widget) => combineLatest([of(x), this._widgetService.getWidgetTypeById(x.widgettype), this.widgetInnerHost$]))
      ).subscribe(([widget, widgetType, host]) => this.loadWidgetInner(widget, widgetType, host))
    ]);
    this.initRights();
  }

  openWidgetMenu() {
    this.widgetComponentService.widget$
      .pipe(switchMap(widget => combineLatest([of(widget), this._widgetService.getWidgetTypeById(widget.widgettype)])), take(1))
      .subscribe(([widget, widgetType]) => {
        var data = new WidgetMenuComponentInputData();
        data.widgetComponent = this;
        let type = WidgetTypeMap[widgetType.wtypename];
        if (!type) return;
        data.type = type.code;
        data.userFilters = widget.widgetuserfilters;
        data.roleFilters = widget.widgetrolefilters;
        data.chartViews = this.widgetComponentService.chartViews;
        if (this.widgetComponentService.contentType) data.showUserFilterOption = true;
        else data.showUserFilterOption = false;
        data.showRoleFilterOption = false;

        let widgetTemplate = globals.widgets[widgetType.wtypename];
        if (widgetTemplate) data.typename = widgetTemplate.pretty;
        this._dialog.open(WidgetMenuComponent, {
          panelClass: "no-padding-dialog-container",
          autoFocus: false,
          data: data,
          viewContainerRef: this._viewContainerRef
        });
      });
  }

  openWidgetUserFilterDialog(identifier: number = null) {
    this._dialog.open(UserFilterDialogComponent, {
      panelClass: "no-padding-dialog-container",
      autoFocus: false,
      data: identifier,
      viewContainerRef: this._viewContainerRef
    }).afterClosed().subscribe({
      next: (result: UserFilterDialogResult) => {
        if(result){
          let text = `Ein Filter wurde ${result.operation === 'CREATE'? 'erzeugt': 'geändert.'}`;
          if(result.sharedWithUser.length > 0) text += ` Der Filter wurde zusätzlich mit ${result.sharedWithUser.length} Nutzern geteilt (${result.sharedWithUser.map(x => x.userName).join(', ')}).`;
          this._snackBar.open(text, 'Ok', { duration: 2000 });
        }
      }
    });
  }

  userFilterClicked = (filter: WidgetUserFilter) => this.componentRef.instance.receiveUserFilter(filter);

  updateRefreshInterval() {
    if (this.refreshSub) this.refreshSub.unsubscribe();
    if (this.refreshMinutes) this.refreshSub = combineLatest([interval(this.refreshMinutes * 60000), this.widgetComponentService.widget$])
      .pipe(switchMap(([s, widget]) => combineLatest([of(s), of(widget), this._widgetService.getWidgetTypeById(widget.widgettype), this.widgetInnerHost$])))
      .subscribe(([s, widget, widgetType, host]) => {
        this._refreshing.next(true);
        timer(1000).subscribe(() => this._refreshing.next(false));
        if (typeof this.componentRef.instance.refresh === 'function') this.componentRef.instance.refresh();
        else this.loadWidgetInner(widget, widgetType, host);
      });
  }

  widgetInnerViewClicked(view: WidgetInnerView) {
    if (typeof view.onClick === 'function') view.onClick().toPromise();
  }

  WidgetBarPosition = WidgetBarPosition;
  WidgetViewStandaloneModes = WidgetViewStandaloneModes;

}

export class CustomChartView {
  widgetsettingid: number;
  widgetid: number;
  href: string;
  name: string;
}
